import { GetServerSideProps } from 'next';

import { sendCounterMetric } from 'utils/metrics';
import { SanityArticleData } from 'lib/sanity/types';
import { getPreviewArticle, getPublishedArticle, INVALID_MARKET, INVALID_SLUG } from 'lib/sanity';
import { cleanSlug, isPreviewSecretValidForArticleSlug } from 'utils/url-helpers/url-helpers';
import Article from 'components/Article';
import { trackException } from 'utils/exceptionHandler';
import { getSharedPageData } from 'utils/shared-page-data';
import createVariantResolver from 'utils/remote-config/content/create-variant-resolver';
import { CMSDataRequest } from 'types/sanity-request';
import { assertIsVertical } from 'utils/type-guards';
import { getVerticalConfig, Vertical, VerticalNotLaunchedError } from 'utils/vertical';

const getRedirectUrl = (slugInUrl: string, correctSlug: string, market: string, isPlain = false) => {
  if (slugInUrl === correctSlug) {
    return null;
  }
  // If the slug is outdated, we redirect to the new one
  return `/${market}/article/${correctSlug}/${isPlain ? 'plain/' : ''}`;
};

const getCMSData = async ({ articleSlug, market, isPreview, vertical }: CMSDataRequest): Promise<SanityArticleData> => {
  const articleSlugLowerCase = articleSlug.toLowerCase();

  let article;
  if (isPreview) {
    article = await getPreviewArticle({ slug: articleSlugLowerCase, market, vertical });
  } else {
    article = await getPublishedArticle({ slug: articleSlugLowerCase, market, vertical });
  }

  if (!article?.content) {
    throw new Error('404');
  }

  return article;
};

export const getServerSideProps: GetServerSideProps = async context => {
  const { req, res, query, resolvedUrl } = context;
  const market = query.market as string;
  const previewSecret = query.previewsecret as string;
  const variantKey = query.variant as string;
  let vertical: Vertical | undefined;

  try {
    const articleSlug = cleanSlug(query.articleslug as string);
    const isPlain = resolvedUrl.endsWith('/plain/');
    const isPreview =
      Boolean(previewSecret) && (await isPreviewSecretValidForArticleSlug(previewSecret, articleSlug.toLowerCase()));

    vertical = assertIsVertical(query.vertical);

    // GetSharedPageData takes a significant amount of time to resolve (due to remote config resolution)
    // To decrease request latency we want to run this in the background.
    const sharedPageDataPromise = getSharedPageData(context);

    const resolveVariant = createVariantResolver(vertical, articleSlug, {
      userId: req.headers['sp-internal-user-id'] as string | undefined,
      installationId: req.headers['sp-internal-tracking-id'] as string,
      isPreview,
      variantKey,
      req,
    });

    const article = await getCMSData({ articleSlug, market, isPreview, vertical });

    const redirectUrl = getRedirectUrl(articleSlug, article.slugs[0], market, isPlain);
    if (redirectUrl) {
      sendCounterMetric('article-redirect', { redirect: 'redirect', vertical });
      return {
        redirect: { destination: redirectUrl, permanent: false },
      };
    }
    sendCounterMetric('article-redirect', { redirect: 'no-redirect', vertical });

    const resolvedArticle = await resolveVariant(article);

    const sharedPageData = await sharedPageDataPromise;

    return {
      props: {
        article: resolvedArticle,
        articleSlug,
        sharedPageData,
      },
    };
  } catch (error) {
    if (error instanceof VerticalNotLaunchedError) {
      return {
        redirect: {
          destination: getVerticalConfig(error.vertical).rolloutConfig!.fallbackUrl,
          permanent: false,
        },
      };
    }
    if (error.message === '404' || error.message === INVALID_SLUG || error.message === INVALID_MARKET) {
      return {
        notFound: true,
      };
    }

    trackException(error, 'article-page', { market, vertical });
    res.writeHead(500).end();
    return { props: {} };
  }
};

export default Article;
